/**
 * Shared Operational Readiness adapter helpers and row types.
 * Presentation mapping only — no business rules.
 */

import {OperationalStatusCardView} from '../operational-readiness.models';

export type BridgeReadinessRow = {
    id?: string;
    bridge_name?: string;
    is_active?: boolean;
    primary_masked_credential?: string | null;
    last_sync_at?: string | null;
    last_sync_status?: string | null;
};

/** Device Health API row used by Operational Readiness Bridge Connected. */
export type DeviceHealthReadinessRow = {
    device_type?: string | null;
    device_id?: string | null;
    name?: string | null;
    mapping_status?: string | null;
    organization_id?: number | null;
    connection_status?: string | null;
    cloud_connection_status?: string | null;
    edge_device_status?: string | null;
    primary_status?: string | null;
    kyra_bridge_id?: string | null;
    kyra_camera_id?: number | null;
};

/**
 * Device Health payload for Operational Readiness.
 * Outer null on the adapter input means the request failed / could not be loaded.
 */
export type DeviceHealthReadinessPayload = {
    status?: string | null;
    detail?: string | null;
    last_updated_at?: string | null;
    results?: DeviceHealthReadinessRow[] | null;
};

export type CameraListAnalyticsItem = {
    analytics_key: string;
    name?: string;
};

export type CameraDeviceHealthStatus = {
    primary_status?: string | null;
    connection_status?: string | null;
    mapping_status?: string | null;
} | null;

export type CameraListStatus = {
    analytics_configured?: boolean;
    enabled_analytics_count?: number;
    enabled_analytics?: CameraListAnalyticsItem[];
    vehicle_detection_enabled?: boolean;
    correlation_window_seconds?: number | null;
    device_health?: CameraDeviceHealthStatus;
};

export type CameraReadinessRow = {
    id?: number;
    camera_esn?: string;
    is_active?: boolean;
    status?: CameraListStatus;
};

export function anyLoadedCameraHasEnabledAnalytics(cameras: CameraReadinessRow[]): boolean {
    return cameras.some((camera) => (camera.status?.enabled_analytics_count || 0) > 0);
}

export function anyLoadedCameraHasVehicleDetection(cameras: CameraReadinessRow[]): boolean {
    return cameras.some((camera) => Boolean(camera.status?.vehicle_detection_enabled));
}

export function eligibleCameraCorrelationWindowSeconds(
    cameras: CameraReadinessRow[],
): number | null {
    for (const camera of cameras) {
        if (!camera.status?.vehicle_detection_enabled) {
            continue;
        }
        const windowSeconds = camera.status.correlation_window_seconds;
        if (typeof windowSeconds === 'number') {
            return windowSeconds;
        }
    }
    return null;
}

export function countReadyCards(cards: OperationalStatusCardView[]): number {
    return cards.filter((card) => card.status === 'ready').length;
}

export function extractPaginatedResults<T>(response: unknown): T[] {
    if (!response) {
        return [];
    }
    if (Array.isArray(response)) {
        return response as T[];
    }
    const results = (response as { results?: unknown }).results;
    return Array.isArray(results) ? (results as T[]) : [];
}
